import React, { useEffect, useRef, useState } from 'react';
import './store.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { PrevButton, NextButton, LoadingImage } from './styles';
import logo from '../../assets/logos/bolt.png'
import { cart, formatCents, productImageUrls, useCart, variantLabel } from '../../features/marketplace';
import type { Product } from '../../features/marketplace';
import { checkoutPanel } from '../../features/marketplace/checkout';

const SWIPE_PX = 40;

// "creator: nassos | created: Sun Sep 20 2026", from the product's 450labs metafields when set.
function sideLine(product: Product): string {
  const meta = product.metafields ?? {};
  const creator = typeof meta.creator === 'string' && meta.creator ? meta.creator : 'nassos';
  const made = typeof meta.created === 'string' ? new Date(meta.created) : null;
  const created = made && !isNaN(made.getTime()) ? ` | created: ${made.toDateString()}` : '';
  return `creator: ${creator}${created}`;
}

const StoreCard: React.FC<{ product: Product; index: number }> = ({ product, index }) => {
  const images = productImageUrls(product);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loaded, setLoaded] = useState<Record<number, boolean>>({});
  const [variantId, setVariantId] = useState<string | undefined>(product.variants?.[0]?.id);
  const [justAdded, setJustAdded] = useState(false);
  const touchX = useRef<number | null>(null);

  const lines = useCart();
  const inCart = lines
    .filter((l) => l.productId === product.id)
    .reduce((sum, l) => sum + l.quantity, 0);

  const variants = product.variants ?? [];
  const variant = variants.find((v) => v.id === variantId);
  const priceCents = product.price_cents + (variant?.price_delta_cents ?? 0);
  const metaSize = typeof product.metafields?.size === 'string' ? product.metafields.size : null;

  useEffect(() => {
    if (!justAdded) return;
    const t = setTimeout(() => setJustAdded(false), 1200);
    return () => clearTimeout(t);
  }, [justAdded]);

  const goToNextImage = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const goToPrevImage = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchX.current === null || images.length < 2) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (dx <= -SWIPE_PX) goToNextImage();
    else if (dx >= SWIPE_PX) goToPrevImage();
  };

  const clickedBuy = () => {
    if (!product.available) return;
    cart.add(product.id, variantId);
    setJustAdded(true);
  };

  const buyText = !product.available ? 'SOLD OUT' : justAdded ? 'ADDED' : 'BUY';

  return (
    <div className='store' style={{ '--i': index } as React.CSSProperties}>
      <div className="store-title">{product.name}</div>
      <div className='grid-im-creator'>
        <div className="rotated-text">{sideLine(product)}</div>
        <div className='im-container' onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          {!loaded[0] && (
            <div className='im-loading'>
              <LoadingImage src={logo} alt="" />
            </div>
          )}
          <div className='im-track' style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
            {images.map((src, i) => (
              <img
                key={src}
                src={src}
                alt={i === 0 ? product.name : `${product.name}, photo ${i + 1}`}
                className={loaded[i] ? 'is-loaded' : ''}
                loading={i === 0 ? 'eager' : 'lazy'}
                draggable={false}
                onLoad={() => setLoaded((prev) => ({ ...prev, [i]: true }))}
              />
            ))}
          </div>
          {images.length > 1 && (
            <>
              <PrevButton className='prev-button' onClick={goToPrevImage} aria-label="Previous photo">
                <FontAwesomeIcon icon={faChevronLeft as any} />
              </PrevButton>
              <NextButton className='next-button' onClick={goToNextImage} aria-label="Next photo">
                <FontAwesomeIcon icon={faChevronRight as any} />
              </NextButton>
              <div className='im-dots' aria-hidden="true">
                {images.map((src, i) => (
                  <span key={src} className={i === currentIndex ? 'is-active' : ''} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      <div className='grid-desc-pr-size' >
        <div className='store-description'>
          {product.description}
        </div>
        <div className='price'>
          {formatCents(priceCents, product.currency)}
        </div>
        {variants.length > 1 ? (
          <div className='size size-picker' role="radiogroup" aria-label="Size">
            {variants.map((v) => (
              <button
                key={v.id}
                type="button"
                role="radio"
                aria-checked={v.id === variantId}
                className={`size-option ${v.id === variantId ? 'is-selected' : ''}`}
                onClick={() => setVariantId(v.id)}
              >
                {variantLabel(v)}
              </button>
            ))}
          </div>
        ) : variants.length === 1 || metaSize ? (
          <div className='size'>
            SZ: {variants.length === 1 ? variantLabel(variants[0]) : metaSize}
          </div>
        ) : null}
      </div>
      <div className='grid-buy-like' >
        <button
          type="button"
          className={`buyButton ${justAdded ? 'added' : ''} ${!product.available ? 'sold-out' : ''}`}
          onClick={clickedBuy}
          disabled={!product.available}
        >
          <span className='stretchedText'>{buyText}</span>
        </button>
        <div className='cart-slot'>
          {inCart > 0 ? (
            <button
              type="button"
              key={inCart}
              className='cart-slot-box has-items pop'
              onClick={checkoutPanel.open}
              aria-label={`${inCart} in cart, open checkout`}
            >
              ({inCart})
            </button>
          ) : (
            <div className='cart-slot-box' aria-label="0 in cart">
              (0)
            </div>
          )}
          <span className='cart-slot-label'>in cart</span>
        </div>
      </div>
    </div>
  );
};

export default StoreCard;
